import React, { ReactElement, ReactNode, useMemo } from 'react';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import { siteClient } from 'shared/domain/site/siteClient';
import { SiteOnView } from 'shared/domain/site/types/view';
import { useEntityById } from 'store/entityCollection';
import { EntityName } from 'shared/domain/entityName';
import { useLevels } from 'store/level';
import { useProjectOrganizationId } from 'store/project';
import { useSites } from 'store/site';
import { siteModelToSiteOnView } from 'views/site/mapping';

interface SiteContextType {
  site: SiteOnView | undefined;
  loading: boolean;
}

const SiteContext = React.createContext<SiteContextType | undefined>(undefined);

/** The site of the URL with its levels; archived sites too. */
const WithSite: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const id = useAppUrlParams(UrlParamName.SITE_ID);
  const { items: sites } = useSites();
  const { item: siteModel, loading: loadingSite } = useEntityById({
    id,
    client: siteClient,
    items: sites,
    entityName: EntityName.sites,
  });
  const { levelsPerSite, loading: loadingLevels } = useLevels();
  const organizationId = useProjectOrganizationId();

  const ctx = useMemo((): SiteContextType => {
    const loading = loadingSite || loadingLevels;
    return {
      site: siteModel && !loading ? siteModelToSiteOnView(siteModel, levelsPerSite, organizationId) : undefined,
      loading,
    };
  }, [siteModel, loadingSite, loadingLevels, levelsPerSite, organizationId]);

  return <SiteContext.Provider value={ctx}>{children}</SiteContext.Provider>;
};

export function useSite(): SiteContextType {
  const context = React.useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within an SiteContextProvider');
  }
  return context;
}

export const withSite =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithSite>
      <Component {...props} />
    </WithSite>
  );
